/**
 * Flowable Moddle Descriptor
 *
 * Defines the Flowable namespace for BPMN extensions.
 * These extensions include properties for:
 * - Service Tasks (class, expression, delegateExpression, topic)
 * - User Tasks (assignee, candidateUsers, candidateGroups, dueDate, formKey)
 * - Execution Listeners
 * - Task Listeners
 * - Field Injections
 */

export const FLOWABLE_NAMESPACE = 'http://flowable.org/bpmn';
export const FLOWABLE_PREFIX = 'flowable';

/**
 * Flowable Moddle Descriptor for bpmn-js
 */
export const flowableModdleDescriptor = {
  name: 'Flowable',
  uri: FLOWABLE_NAMESPACE,
  prefix: FLOWABLE_PREFIX,
  xml: {
    tagAlias: 'lowerCase',
  },
  types: [
    // Service Task Implementation
    {
      name: 'ServiceTaskLike',
      extends: ['bpmn:ServiceTask'],
      properties: [
        {
          name: 'class',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'expression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'delegateExpression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'resultVariable',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'topic',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'type',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'async',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'exclusive',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    // User Task Assignment
    {
      name: 'UserTaskLike',
      extends: ['bpmn:UserTask'],
      properties: [
        {
          name: 'assignee',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'candidateUsers',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'candidateGroups',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'dueDate',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'priority',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'formKey',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'formFieldValidation',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'skipExpression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'async',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    // Script Task
    {
      name: 'ScriptTaskLike',
      extends: ['bpmn:ScriptTask'],
      properties: [
        {
          name: 'resultVariable',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'autoStoreVariables',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    // Call Activity
    {
      name: 'CallActivityLike',
      extends: ['bpmn:CallActivity'],
      properties: [
        {
          name: 'calledElement',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'calledElementBinding',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'calledElementVersion',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'inheritVariables',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'fallbackToDefaultTenant',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    // Process Properties
    {
      name: 'ProcessLike',
      extends: ['bpmn:Process'],
      properties: [
        {
          name: 'candidateStarterUsers',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'candidateStarterGroups',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'historyTimeToLive',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'isStartableInTasklist',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    // Sequence Flow Condition
    {
      name: 'SequenceFlowLike',
      extends: ['bpmn:SequenceFlow'],
      properties: [
        {
          name: 'skipExpression',
          isAttr: true,
          type: 'String',
        },
      ],
    },
    // Gateway Extensions
    {
      name: 'GatewayLike',
      extends: ['bpmn:ExclusiveGateway', 'bpmn:InclusiveGateway'],
      properties: [
        {
          name: 'async',
          isAttr: true,
          type: 'Boolean',
        },
      ],
    },
    // Execution Listener
    {
      name: 'ExecutionListener',
      superClass: ['Element'],
      properties: [
        {
          name: 'event',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'class',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'expression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'delegateExpression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'fields',
          isMany: true,
          type: 'Field',
        },
      ],
    },
    // Task Listener
    {
      name: 'TaskListener',
      superClass: ['Element'],
      properties: [
        {
          name: 'event',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'class',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'expression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'delegateExpression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'fields',
          isMany: true,
          type: 'Field',
        },
      ],
    },
    // Field Injection
    {
      name: 'Field',
      superClass: ['Element'],
      properties: [
        {
          name: 'name',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'expression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'stringValue',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'string',
          type: 'String',
        },
      ],
    },
    // In/Out Mapping for Call Activity
    {
      name: 'In',
      superClass: ['Element'],
      properties: [
        {
          name: 'source',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'sourceExpression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'target',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'variables',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'businessKey',
          isAttr: true,
          type: 'String',
        },
      ],
    },
    {
      name: 'Out',
      superClass: ['Element'],
      properties: [
        {
          name: 'source',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'sourceExpression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'target',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'variables',
          isAttr: true,
          type: 'String',
        },
      ],
    },
    // Form Property (legacy)
    {
      name: 'FormProperty',
      superClass: ['Element'],
      properties: [
        {
          name: 'id',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'name',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'type',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'required',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'readable',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'writeable',
          isAttr: true,
          type: 'Boolean',
        },
        {
          name: 'variable',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'expression',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'default',
          isAttr: true,
          type: 'String',
        },
        {
          name: 'datePattern',
          isAttr: true,
          type: 'String',
        },
      ],
    },
  ],
  associations: [],
};
